/**
 * The delinquency balance, bucketed 0–30/31–60/61–90/90+ days late — the
 * bucket columns CLAUDE.md's step 9 adds on top of what step 6 already
 * built. A separate endpoint from Delinquency/`ledger/delinquency` rather
 * than an extension of it: that page and its `SessionBalance` shape are
 * shared with the dashboards and both portals, and bucket columns don't
 * belong on any of those.
 *
 * One tab inside `ReportsHome`'s unified interface, not its own routed
 * page — no page chrome here beyond the description, since the tab bar and
 * heading are the host's job. Owner/tenant scope is owned by the host too
 * (persists across tabs); this component only manages its own property
 * filter.
 */

import { useState } from 'react';
import { useList } from '@refinedev/core';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { ReportFilterBar } from '../../../components/ReportFilterBar.js';
import {
  StackedBarChart,
  type StackedBarDatum,
} from '../../../components/charts/StackedBarChart.js';

interface ArAgingRow {
  leaseId: string;
  propertyName: string;
  unitLabel: string;
  totalBalance: string;
  bucket0to30: string;
  bucket31to60: string;
  bucket61to90: string;
  bucket90plus: string;
  maxDaysLate: number;
  /** PENDING/PROCESSING payments on this lease — not yet in any bucket above. */
  pendingPaymentAmount: string;
}

const columns: Column<ArAgingRow>[] = [
  { key: 'property', header: 'Property', render: (r) => r.propertyName },
  { key: 'unit', header: 'Unit', render: (r) => r.unitLabel },
  { key: 'b0', header: '0–30', numeric: true, render: (r) => `$${r.bucket0to30}` },
  { key: 'b31', header: '31–60', numeric: true, render: (r) => `$${r.bucket31to60}` },
  { key: 'b61', header: '61–90', numeric: true, render: (r) => `$${r.bucket61to90}` },
  { key: 'b90', header: '90+', numeric: true, render: (r) => `$${r.bucket90plus}` },
  { key: 'total', header: 'Total', numeric: true, render: (r) => `$${r.totalBalance}` },
  {
    key: 'processing',
    header: 'Processing',
    numeric: true,
    render: (r) => (Number(r.pendingPaymentAmount) > 0 ? `$${r.pendingPaymentAmount}` : '—'),
  },
];

const BUCKET_KEYS = [
  { key: 'bucket0to30', label: '0–30d' },
  { key: 'bucket31to60', label: '31–60d' },
  { key: 'bucket61to90', label: '61–90d' },
  { key: 'bucket90plus', label: '90+d' },
] as const;

function money(value: number): string {
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

/** Per-lease rows collapsed to one bar per property, bucket totals summed —
 *  the table already shows the per-lease detail, the chart is for comparing
 *  properties against each other. */
function byProperty(rows: ArAgingRow[]): StackedBarDatum[] {
  const totals = new Map<string, Record<(typeof BUCKET_KEYS)[number]['key'], number>>();
  for (const row of rows) {
    const entry = totals.get(row.propertyName) ?? {
      bucket0to30: 0,
      bucket31to60: 0,
      bucket61to90: 0,
      bucket90plus: 0,
    };
    for (const { key } of BUCKET_KEYS) entry[key] += Number(row[key]);
    totals.set(row.propertyName, entry);
  }
  return Array.from(totals.entries()).map(([propertyName, buckets]) => ({
    category: propertyName,
    segments: BUCKET_KEYS.map(({ key, label }) => ({
      key,
      label,
      value: buckets[key],
      displayValue: money(buckets[key]),
    })),
  }));
}

export function ArAging({
  ownerId,
  onOwnerChange,
  tenantId,
  onTenantChange,
}: {
  ownerId: string;
  onOwnerChange: (value: string) => void;
  tenantId: string;
  onTenantChange: (value: string) => void;
}) {
  const [propertyId, setPropertyId] = useState('');

  const { data, isLoading, error } = useList<ArAgingRow>({
    resource: 'reports/ar-aging',
    pagination: { mode: 'off' },
    filters: [
      { field: 'propertyId', operator: 'eq', value: propertyId || undefined },
      { field: 'ownerId', operator: 'eq', value: ownerId || undefined },
      { field: 'tenantId', operator: 'eq', value: tenantId || undefined },
    ],
  });

  const rows = data?.data ?? [];

  return (
    <div className="stack">
      <p className="muted">
        Today's open balance, bucketed by how late it is. A charge not yet due never appears here —
        only the amount already past its due date is bucketed.
      </p>

      <ReportFilterBar
        propertyId={propertyId}
        onPropertyChange={setPropertyId}
        ownerId={ownerId}
        onOwnerChange={onOwnerChange}
        tenantId={tenantId}
        onTenantChange={onTenantChange}
        exportPath="/reports/ar-aging"
        exportParams={{
          propertyId: propertyId || undefined,
          ownerId: ownerId || undefined,
          tenantId: tenantId || undefined,
        }}
      />

      {!isLoading && !error && rows.length > 0 ? (
        <div className="card">
          <p className="muted">Balance by property, bucketed the same as the table below.</p>
          <StackedBarChart data={byProperty(rows)} colorVar="--down" />
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.leaseId}
        isLoading={isLoading}
        error={error}
        emptyMessage="Nothing outstanding."
      />
    </div>
  );
}
